/**
 * The app's chart colours. Each is a CSS variable from index.css with its own
 * dark-mode value, so a chart config takes one colour, never a light/dark
 * pair.
 *
 * Series are shades of the site's navy, darkest first: a chart's series are
 * spread over the ramp so that, however many there are, a stack builds up from
 * dark to light. A colour that means something rather than telling series
 * apart (a status, "could not tell", the previous period) takes its role in
 * `chartColor`.
 */

/** The shades index.css defines (`--theme-1..7`), darkest first. */
export const SERIES_COLOR_COUNT = 7;

// Literal, so Tailwind sees every class
const SERIES_CLASSES = [
    "bg-theme-1",
    "bg-theme-2",
    "bg-theme-3",
    "bg-theme-4",
    "bg-theme-5",
    "bg-theme-6",
    "bg-theme-7",
];

/**
 * Which shade series `index` of `count` takes. One series takes the middle
 * shade. Up to six spread over the first six, leaving the palest, which is too
 * faint on white for a bar of its own, to a chart that needs all seven.
 */
function seriesShade(index: number, count: number): number {
    if (count <= 1) {
        return 4;
    }
    const last = Math.min(
        Math.max(count, SERIES_COLOR_COUNT - 1),
        SERIES_COLOR_COUNT,
    );
    return (
        1 + Math.round((Math.min(index, count - 1) * (last - 1)) / (count - 1))
    );
}

/** The colour of series `index` (0-based) of `count`, optionally see-through. */
export function seriesColor(
    index: number,
    count: number,
    alpha?: number,
): string {
    // A lone series has its own colour, the site's Blue 500 in both modes
    const channels =
        count <= 1 ? "--chart-single" : `--theme-${seriesShade(index, count)}`;
    return alpha === undefined
        ? `hsl(var(${channels}))`
        : `hsl(var(${channels}) / ${alpha})`;
}

/** The same as a Tailwind background class. */
export function seriesClass(index: number, count: number): string {
    return count <= 1
        ? "bg-chart-single"
        : SERIES_CLASSES[seriesShade(index, count) - 1]!;
}

/** A heatmap cell at `intensity` (0 to 1): blue, or red, a loss, below zero. */
export function heatColor(intensity: number, negative = false): string {
    const channels = negative ? "--chart-heat-negative" : "--chart-heat";
    return `hsl(var(${channels}) / ${intensity})`;
}

/**
 * A chart tooltip's surface: the app's own tooltip (common/Tooltip), light in
 * light mode, but opaque, as saturated marks behind it would tint it.
 */
export const CHART_TOOLTIP_SURFACE =
    "bg-white text-default shadow-lg ring-1 ring-zinc-950/10 dark:bg-zinc-800 dark:ring-inset dark:ring-white/10";

export const chartColor = {
    good: "var(--chart-good)",
    warning: "var(--chart-warning)",
    critical: "var(--chart-critical)",
    /** Counted, but not told apart: unknown, unset, unattributed. */
    muted: "var(--chart-muted)",
    /** Nothing to tell: no value, not covered. */
    faint: "var(--chart-faint)",
    /** A third neutral, for a chart that needs one past `faint`. */
    fainter: "var(--chart-fainter)",
    /** The previous period, drawn under the current one. */
    compare: "var(--chart-compare)",
    /** The text colour, for a line that is no series: a second metric over bars. */
    ink: "var(--chart-ink)",
} as const;
